import { Users2Icon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { FormDateFieldDS, FormNumberFieldDS, FormTextFieldDS } from 'components/reusable/form/FormFields'
import { Button, ButtonGroup, CollapsibleContainer, TextButton, useBreakpoint } from 'slab'
import { numberToOrdinalText } from 'utils/numberUtils'

import { DependentInfoEntry } from './DependentInfoEntry'
import { type EmployeeInfo, type Employees } from './EmployeeEntry'
import { EmployeeInfoEntryHeader } from './EmployeeInfoEntryHeader'

type Props = {
  index: number
  defaultOpen?: boolean
  onRemove: () => void
}

export function EmployeeInfoEntry ({ index, defaultOpen = true, onRemove }: Props) {
  const { breakpointIsAtLeast } = useBreakpoint()
  const { control, register, getValues, trigger, formState: { errors, submitCount, isSubmitting } } = useFormContext<Employees>()

  const [savedEmployee, setSavedEmployee] = useState<EmployeeInfo | undefined>(getValues(`employees.${index}`).name ? getValues(`employees.${index}`) : undefined)
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const { fields: dependentFields, append: appendDependent, remove: removeDependent } = useFieldArray({
    control,
    name: `employees.${index}.dependents`
  })

  useEffect(() => {
    if (errors.employees?.[index]) {
      setIsOpen(true)
    }
  }, [errors, submitCount])

  const onClickAddEmployee = async () => {
    const isValid = await trigger(`employees.${index}`)
    if (isValid) {
      setSavedEmployee(getValues(`employees.${index}`))
      setIsOpen(false)
    }
  }

  const onClickAddDependent = async () => {
    appendDependent({
      relationship: dependentFields.some(field => field.relationship === 'spouse') ? 'child' : 'spouse',
      date_of_birth: ''
    })
  }

  function renderButtons () {
    const removeButton = (
      <Button
        variant='tertiary-destructive'
        onClick={onRemove}
        disabled={isSubmitting}
      >
        Remove employee
      </Button>
    )

    const addButton = (
      <Button
        variant='secondary'
        onClick={onClickAddEmployee}
        disabled={isSubmitting}
      >
        {savedEmployee ? 'Save changes' : 'Save employee'}
      </Button>
    )

    if (breakpointIsAtLeast('xs')) {
      return (
        <ButtonGroup>
          {removeButton}
          {addButton}
        </ButtonGroup>
      )
    }

    return (
      <ButtonGroup layout='vertical'>
        {addButton}
        {removeButton}
      </ButtonGroup>
    )
  }

  return (
    <div className='flex flex-col gap-2'>
      <h2 className='body-sm-bold text-gray-800'>{numberToOrdinalText(index + 1)} employee</h2>
      <CollapsibleContainer
        heading={<EmployeeInfoEntryHeader employee={savedEmployee} />}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <div className='flex flex-col flex-1 gap-6 p-4'>
          <div className='flex flex-row gap-4 flex-wrap'>
            <div className='flex-1 min-w-56'>
              <FormTextFieldDS<Employees>
                fieldId={`employees.${index}.first_name`}
                fieldLabel='First name'
                register={register}
                placeholder='First name'
                validationRule={{ required: 'First name is required.' }}
                error={errors.employees?.[index]?.first_name}
                disabled={isSubmitting}
                autoFocus={isOpen}
              />
            </div>
            <div className='flex-1 min-w-56'>
              <FormTextFieldDS<Employees>
                fieldId={`employees.${index}.last_name`}
                fieldLabel='Last name'
                register={register}
                placeholder='Last name'
                validationRule={{ required: 'Last name is required.' }}
                error={errors.employees?.[index]?.last_name}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className='flex flex-row gap-4 flex-wrap'>
            <div className='flex-1 min-w-56'>
              <FormTextFieldDS<Employees>
                fieldId={`employees.${index}.email`}
                fieldLabel='Email (optional)'
                register={register}
                placeholder='Personal or work email'
                validationRule={{
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address.'
                  }
                }}
                error={errors.employees?.[index]?.email}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className='flex flex-row gap-4 flex-wrap'>
            <div className='min-w-44 w-1/4'>
              <FormDateFieldDS<Employees>
                fieldId={`employees.${index}.date_of_birth`}
                fieldLabel='Date of birth'
                register={register}
                placeholder='Date of birth'
                validationRule={{
                  required: 'Date of birth is required.',
                  validate: (value: string) => {
                    if (new Date(value) > new Date()) {
                      return 'Date of birth cannot be in the future.'
                    }
                    return true
                  },
                }}
                error={errors.employees?.[index]?.date_of_birth}
                disabled={isSubmitting}
              />
            </div>
            <div className='min-w-44 w-1/4'>
              <FormNumberFieldDS<Employees>
                fieldId={`employees.${index}.zip`}
                fieldLabel='Zip code'
                register={register}
                placeholder='Zip code'
                validationRule={{
                  required: 'Zip code is required.',
                  pattern: { value: /^\d{5}$/, message: 'Zip code must be 5 digits.' }
                }}
                error={errors.employees?.[index]?.zip}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className='flex flex-col gap-4'>
            {dependentFields.map((dependent, dependentIndex) => (
              <DependentInfoEntry
                key={dependent.id}
                employeeIndex={index}
                dependentIndex={dependentIndex}
                onRemove={() => removeDependent(dependentIndex)}
              />
            ))}
            <div>
              <TextButton
                leftIcon={Users2Icon}
                color='gray'
                onClick={onClickAddDependent}
                disabled={isSubmitting}
              >
                Add {dependentFields.length ? 'another' : ''} dependent
              </TextButton>
            </div>
          </div>
        </div>
        <div className='xs:flex xs:justify-end border-t border-gray-200 py-4 px-6'>
          {renderButtons()}
        </div>
      </CollapsibleContainer>
    </div>
  )
}
